import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Users, 
  Mail, 
  Phone, 
  Hash,
  Calendar,
  MapPin,
  CreditCard,
  User,
  UserCheck,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Image as ImageIcon,
  School,
  Book
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const StudentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company?.id && id) {
      loadStudent();
    }
  }, [company, id]);

  const loadStudent = async () => {
    try {
      setLoading(true);
      console.log('Loading student with ID:', id, 'for company:', company.id);
      
      const foundStudent = await dataHelpers.getStudentById(company.id, id);
      
      if (!foundStudent) {
        console.log('Student not found with ID:', id);
        toast.error('Aluno não encontrado');
        navigate('/dashboard/students');
        return;
      }
      
      console.log('Student loaded successfully:', foundStudent);
      setStudent(foundStudent);
    } catch (error) {
      console.error('Error loading student:', error);
      toast.error('Erro ao carregar aluno');
      navigate('/dashboard/students');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir o aluno "${student.people?.name || student.registration_number}"?`)) {
      return;
    }

    try {
      await dataHelpers.deleteStudent(student.id);
      toast.success('Aluno excluído com sucesso');
      navigate('/dashboard/students');
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Erro ao excluir aluno');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Não informado';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatCPF = (cpf) => {
    if (!cpf) return 'Não informado';
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };

  const getPaymentTypeLabel = (type) => {
    switch (type) {
      case 'prepaid':
        return 'Pré-pago';
      case 'credit':
        return 'Crédito';
      case 'monthly':
        return 'Mensalista';
      default:
        return type;
    }
  };

  const getBalanceStatus = () => {
    if (!student.balance) return 'neutral';
    
    const balance = parseFloat(student.balance);
    if (balance > 0) return 'positive';
    if (balance < 0) return 'negative';
    return 'neutral';
  };

  const getBalanceStatusInfo = () => {
    const status = getBalanceStatus();
    
    switch (status) {
      case 'positive':
        return {
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: CheckCircle,
          text: 'Saldo positivo'
        };
      case 'negative':
        return {
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: AlertCircle,
          text: 'Saldo negativo'
        };
      default:
        return {
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          icon: DollarSign,
          text: 'Saldo zerado'
        };
    }
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!student) {
    return (
      
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aluno não encontrado</h2>
          <p className="text-gray-600 mb-6">O aluno que você está procurando não existe ou foi removido.</p>
          <Link to="/dashboard/students">
            <Button>Voltar para Alunos</Button>
          </Link>
        </div>
      
    );
  }

  const balanceStatus = getBalanceStatusInfo();
  const BalanceIcon = balanceStatus.icon;

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/students')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {student.people?.name || 'Aluno'}
              </h1>
              <p className="text-gray-600">
                Matrícula: {student.registration_number} • Turma: {student.class_name}
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Link to={`/dashboard/students/${student.id}/edit`}>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Student Photo and Basic Info */}
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Photo */}
                <div className="flex justify-center">
                  {student.photo_url ? (
                    <img
                      src={student.photo_url}
                      alt={student.people?.name}
                      className="w-full max-w-xs h-64 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="w-full max-w-xs h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <User className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Sem foto</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Aluno</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Nome</p>
                          <p className="font-medium">{student.people?.name || 'Não informado'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Hash className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Matrícula</p>
                          <p className="font-medium">{student.registration_number}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <School className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Turma</p>
                          <p className="font-medium">{student.class_name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Data de Nascimento</p>
                          <p className="font-medium">{formatDate(student.birth_date)}</p>
                        </div>
                      </div>

                      {student.people?.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">Email</p>
                            <p className="font-medium">{student.people.email}</p>
                          </div>
                        </div>
                      )}

                      {student.people?.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">Telefone</p>
                            <p className="font-medium">{formatPhone(student.people.phone)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>            {/* Parent Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Responsável</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <User className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Nome</p>
                      <p className="font-medium">{student.person?.name || 'Não informado'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{student.person?.email || 'Não informado'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Telefone</p>
                      <p className="font-medium">{formatPhone(student.person?.phone)}</p>
                    </div>
                  </div>                    <div className="flex items-center">
                      <Hash className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">CPF</p>
                        <p className="font-medium">{formatCPF(student.person?.cpf || student.person?.document)}</p>
                      </div>
                    </div>
                </div>
              </div>            </Card>

            {/* Financial Parent Information */}
            {student.financial_parent_id && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Responsável Financeiro</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Nome</p>
                        <p className="font-medium">{student.financial_person?.name || 'Não informado'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{student.financial_person?.email || 'Não informado'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Telefone</p>
                        <p className="font-medium">{formatPhone(student.financial_person?.phone)}</p>
                      </div>
                    </div>
                      <div className="flex items-center">
                      <Hash className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">CPF</p>
                        <p className="font-medium">{formatCPF(student.financial_person?.cpf || student.financial_person?.document)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}            {/* Address Information */}
            {(student.address || student.city || student.state || student.person?.address || student.person?.city || student.person?.state) && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h3>
                
                {/* Endereço do Estudante */}
                {(student.address || student.city || student.state) && (
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-gray-800 mb-3">Endereço residencial do aluno</h4>
                    <div className="space-y-3">
                      {student.address && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">Endereço</p>
                            <p className="font-medium">{student.address}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Cidade/Estado</p>
                          <p className="font-medium">
                            {[student.city, student.state].filter(Boolean).join(', ') || 'Não informado'}
                          </p>
                        </div>
                      </div>
                      
                      {student.zip_code && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">CEP</p>
                            <p className="font-medium">{student.zip_code}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Endereço do Responsável */}
                {student.person && (student.person.address || student.person.city || student.person.state) && (
                  <div className={student.address || student.city || student.state ? 'pt-4 border-t border-gray-200' : ''}>
                    <h4 className="text-md font-medium text-gray-800 mb-3">Endereço do responsável</h4>
                    <div className="space-y-3">
                      {student.person.address && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">Endereço</p>
                            <p className="font-medium">{student.person.address}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-600">Cidade/Estado</p>
                          <p className="font-medium">
                            {[student.person.city, student.person.state].filter(Boolean).join(', ') || 'Não informado'}
                          </p>
                        </div>
                      </div>
                      
                      {(student.person.zip_code || student.person.postal_code) && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm text-gray-600">CEP</p>
                            <p className="font-medium">{student.person.zip_code || student.person.postal_code}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Financeiras</h3>
              <div className="space-y-4">
                <div className={`flex justify-between items-center p-3 rounded-lg ${balanceStatus.bgColor}`}>
                  <div>
                    <p className={`text-sm ${balanceStatus.color}`}>Saldo Atual</p>
                    <p className={`text-xl font-bold ${balanceStatus.color}`}>
                      {formatCurrency(student.balance)}
                    </p>
                  </div>
                  <BalanceIcon className={`w-8 h-8 ${balanceStatus.color}`} />
                </div>
                
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm text-blue-700">Limite de Crédito</p>
                    <p className="text-xl font-bold text-blue-800">
                      {formatCurrency(student.credit_limit)}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 text-blue-600" />
                </div>
                
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="text-sm text-purple-700">Tipo de Pagamento</p>
                    <p className="text-lg font-bold text-purple-800">
                      {getPaymentTypeLabel(student.payment_type)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </Card>

            {/* Status */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status do Aluno</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Ativo</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    student.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {student.active ? 'Sim' : 'Não'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Cadastrado em</span>
                  <span className="text-sm text-gray-900">{formatDate(student.created_at)}</span>
                </div>
              </div>
            </Card>

            {/* Notes */}
            {student.notes && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Observações</h3>
                <p className="text-gray-600">{student.notes}</p>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="space-y-3">
                <Link to={`/dashboard/students/${student.id}/edit`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar Aluno
                  </Button>
                </Link>
                
                <Button variant="outline" className="w-full justify-start" disabled>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Gerenciar Saldo
                </Button>
                
                <Button variant="outline" className="w-full justify-start" disabled>
                  <Book className="w-4 h-4 mr-2" />
                  Histórico de Compras
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentDetailsPage;
